import { useCallback, useEffect, useRef } from 'react';

const STALL_CHECK_INTERVAL_MS = 2000;
const STALL_THRESHOLD_MS = 8000;
const MAX_STALL_RECOVERY_ATTEMPTS = 3;

interface VideoBlockProps {
  src: string;
  objectFit?: 'contain' | 'cover';
  rotation?: number;
  pageId?: number;
  boundryId?: string | null;
  isActive?: boolean;
  loop?: boolean;
  onEnded?: () => void;
}

const rotationStyle = {
  [180]: {
    transform: 'rotate(180deg)',
  },
  [90]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(90deg) translateY(-100%)',
    WebkitTransform: 'rotate(90deg) translateY(-100%)',
    MozTransform: 'rotate(90deg) translateY(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
  [270]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(270deg) translateX(-100%)',
    WebkitTransform: 'rotate(270deg) translateX(-100%)',
    MozTransform: 'rotate(270deg) translateX(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
} as const;

function VideoBlock({ src, objectFit, rotation = 0, isActive, loop = true, onEnded }: VideoBlockProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastProgressRef = useRef<{ time: number; at: number }>({ time: 0, at: 0 });
  const stallRecoveryAttemptsRef = useRef(0);

  const clearPlayRetry = () => {
    if (!playRetryTimeoutRef.current) {
      return;
    }

    clearTimeout(playRetryTimeoutRef.current);
    playRetryTimeoutRef.current = null;
  };

  const clearStallWatch = () => {
    if (!stallIntervalRef.current) {
      return;
    }

    clearInterval(stallIntervalRef.current);
    stallIntervalRef.current = null;
  };

  const prepareForAutoplay = (video: HTMLVideoElement) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
  };

  const tryPlay = (video: HTMLVideoElement, retryOnAbort = true) => {
    if (!video.isConnected) {
      return;
    }

    const playResult = video.play();

    if (!playResult || typeof playResult.catch !== 'function') {
      return;
    }

    playResult.catch(err => {
      const isAbort = err?.name === 'AbortError';

      if (isAbort && retryOnAbort) {
        clearPlayRetry();
        playRetryTimeoutRef.current = setTimeout(() => {
          if (!video.isConnected || !video.paused) {
            return;
          }
          tryPlay(video, false);
        }, 80);
        return;
      }

      console.warn('Video playback failed:', err);
    });
  };

  // Release the hardware video decoder. Samsung Tizen has only one decoder,
  // and simply unmounting the element isn't always enough to free it before
  // the next page's video tries to claim it.
  const releaseDecoder = (video: HTMLVideoElement) => {
    try {
      if (!video.paused) {
        video.pause();
      }
      video.removeAttribute('src');
      while (video.firstChild) {
        video.removeChild(video.firstChild);
      }
      video.load();
    } catch (err) {
      console.warn('Video decoder release failed:', err);
    }
  };

  const recoverFromStall = (video: HTMLVideoElement) => {
    if (!video.isConnected) {
      return;
    }

    if (stallRecoveryAttemptsRef.current >= MAX_STALL_RECOVERY_ATTEMPTS) {
      console.warn('Video stall recovery gave up after repeated attempts.');
      clearStallWatch();
      return;
    }

    stallRecoveryAttemptsRef.current += 1;
    console.warn(`Video appears stalled, attempting recovery (${stallRecoveryAttemptsRef.current}).`);

    prepareForAutoplay(video);
    try {
      video.load();
    } catch (err) {
      console.warn('Video reload during stall recovery failed:', err);
    }

    lastProgressRef.current = { time: video.currentTime, at: Date.now() };
    tryPlay(video, false);
  };

  const startStallWatch = (video: HTMLVideoElement) => {
    clearStallWatch();
    lastProgressRef.current = { time: video.currentTime, at: Date.now() };
    stallRecoveryAttemptsRef.current = 0;

    stallIntervalRef.current = setInterval(() => {
      if (!video.isConnected) {
        clearStallWatch();
        return;
      }

      if (video.ended || video.paused) {
        lastProgressRef.current = { time: video.currentTime, at: Date.now() };
        return;
      }

      const now = Date.now();
      if (video.currentTime !== lastProgressRef.current.time) {
        lastProgressRef.current = { time: video.currentTime, at: now };
        stallRecoveryAttemptsRef.current = 0;
        return;
      }

      if (now - lastProgressRef.current.at >= STALL_THRESHOLD_MS) {
        recoverFromStall(video);
      }
    }, STALL_CHECK_INTERVAL_MS);
  };

  // Fires exactly when the <video> element mounts (el) or unmounts (null).
  // Using a ref callback (instead of effect cleanup) lets us release the
  // decoder right as React removes the element, without interfering with
  // src updates that reuse the same element.
  const handleVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el) {
      videoRef.current = el;
      return;
    }

    const prev = videoRef.current;
    if (!prev) {
      return;
    }

    clearPlayRetry();
    clearStallWatch();
    releaseDecoder(prev);
    videoRef.current = null;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    clearPlayRetry();
    clearStallWatch();
    prepareForAutoplay(video);

    if (isActive === false) {
      return;
    }

    video.load();
    startStallWatch(video);
    return () => {
      clearPlayRetry();
      clearStallWatch();
    };
  }, [isActive, src]);

  // Only render the <video> element for the active page.
  // Samsung Tizen only supports a single hardware video decoder - multiple
  // <video> elements in the DOM simultaneously cause decoder conflicts.
  if (isActive === false) {
    return <div className="w-full h-full relative" />;
  }

  return (
    <div className="w-full h-full relative">
      <video
        ref={handleVideoRef}
        controls={false}
        autoPlay
        playsInline
        loop={loop}
        preload="auto"
        muted
        onEnded={() => {
          if (!loop) {
            onEnded?.();
          }
        }}
        onCanPlay={() => {
          const video = videoRef.current;
          if (!video || !video.paused) {
            return;
          }

          prepareForAutoplay(video);
          tryPlay(video, false);
        }}
        className="w-full h-full"
        style={{
          objectFit: objectFit ?? 'contain',
          backgroundColor: 'transparent',
          ...(rotation !== 0 ? rotationStyle[rotation.toString() as unknown as keyof typeof rotationStyle] : {}),
        }}
      >
        <source src={src} type="video/mp4" />
        <source src={src} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export default VideoBlock;
